import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { maintenanceApi } from "@/api/maintenanceApi"
import { MaintenanceForm } from "../components/MaintenanceForm"
import { FormPageLayout } from "@/shared/components/forms"
import { PageLoading } from "@/shared/components/ui/LoadingSpinner"
import { Card } from "@/shared/components/ui/Card"
import { Button } from "@/shared/components/ui/Button"
import { useFarmFromUrl } from "@/shared/hooks/useUrlSync"
import { pondsApi } from "@/features/ponds/api/pondsApi"
import { queryKeys } from "@/shared/api/queryClient"
import { buildRoute } from "@/shared/lib/constants"
import { toApiRequest, type MaintenanceFormData } from "../schemas/maintenanceSchema"

export function EditMaintenancePage() {
  const { checkId } = useParams<{ checkId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  const { farmId, isLoading: isFarmLoading } = useFarmFromUrl()
  const pondId = searchParams.get("pondId")

  // Fetch pond data using query params
  const { data: pond, isLoading: isPondLoading } = useQuery({
    queryKey: queryKeys.ponds.detail(farmId || "", pondId || ""),
    queryFn: () => pondsApi.get(farmId!, pondId!),
    enabled: !!farmId && !!pondId,
  })

  // Fetch existing check data
  const { data: check, isLoading: isCheckLoading } = useQuery({
    queryKey: ["maintenance", farmId, pondId, checkId],
    queryFn: () => maintenanceApi.getCheck(farmId!, pondId!, checkId!),
    enabled: !!farmId && !!pondId && !!checkId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: MaintenanceFormData) => {
      if (!farmId || !pondId || !checkId) throw new Error("Farm, pond, and check required")
      return maintenanceApi.update(farmId, pondId, checkId, toApiRequest(data))
    },
    onSuccess: () => {
      if (pondId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all(pondId) })
      }
      if (farmId && pondId && checkId) {
        queryClient.invalidateQueries({
          queryKey: ["maintenance", farmId, pondId, checkId],
        })
      }
      handleCancel()
    },
  })

  const handleCancel = () => {
    if (farmId && checkId) {
      navigate(buildRoute.maintenanceDetail(farmId, checkId, pondId))
    } else if (farmId) {
      navigate(buildRoute.maintenance(farmId, pondId))
    }
  }

  const handleSubmit = async (data: MaintenanceFormData) => {
    await updateMutation.mutateAsync(data)
  }

  if (isFarmLoading || isPondLoading || isCheckLoading) {
    return <PageLoading message="Loading maintenance check..." />
  }

  if (!check) {
    return (
      <Card>
        <div className="text-center py-16">
          <h3 className="font-roboto text-base font-semibold text-gray-900">
            Check not found
          </h3>
          <p className="font-roboto text-sm text-gray-600 mt-2">
            The maintenance check you're trying to edit doesn't exist.
          </p>
          <Button onClick={handleCancel} className="mt-4">
            Back to Maintenance
          </Button>
        </div>
      </Card>
    )
  }

  // Convert API response to form default values
  const defaultValues: Partial<MaintenanceFormData> = {
    checkedAt: new Date(check.checkedAt),
    waterLevel: check.waterLevel,
    waterFlowCheck: check.waterFlowCheck,
    paddleWheelFunctioning: check.paddleWheelFunctioning,
    stressedFishAtInlets: check.stressedFishAtInlets,
    algalBloom: check.algalBloom,
    presenceOfBirds: check.presenceOfBirds,
    procedureFollowed: check.procedureFollowed ?? "",
    dischargePh: check.dischargePh ?? undefined,
    dischargeDo: check.dischargeDo ?? undefined,
    dischargeFlowRate: check.dischargeFlowRate ?? undefined,
    notes: check.notes ?? "",
    imageIds: check.imageIds ?? [],
  }

  return (
    <FormPageLayout
      title="Edit Maintenance Check"
      subtitle={pond?.name}
      onCancel={handleCancel}
      onSubmit={() => {
        document.getElementById("maintenance-form")?.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        )
      }}
      isSubmitting={updateMutation.isPending}
      submitLabel="Save Changes"
      cancelLabel="Cancel"
    >
      <MaintenanceForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
      />
    </FormPageLayout>
  )
}
